import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import config from '../../../../../config/config';
import { UrlCryptoService } from '../../../core/data-access/services/url-crypto.service';
import { CentroSeleccionado, mismoCentro, RUTA_CENTRO, RUTA_EMPRESA } from '../models/centro-seleccionado';

/**
 * Empresa y centro de trabajo elegidos en vigilancia de la salud. Salen de la URL (centroSeleccionadoGuard),
 * así que sobreviven a recargar la página o abrir el enlace en otra pestaña, y viajan en cada petición a la
 * API del módulo (centroSeleccionadoInterceptor).
 */
@Injectable({
  providedIn: 'root'
})
export class SeleccionCentroService {
  private readonly seleccion$ = new BehaviorSubject<CentroSeleccionado | null>(null);

  constructor(private readonly urlCrypto: UrlCryptoService) { }

  /** null fuera de un centro (en el listado de empresas o en otro módulo). */
  get actual(): CentroSeleccionado | null {
    return this.seleccion$.value;
  }

  /** El centro actual y, después, cada cambio de centro (no repite el mismo). */
  get cambios(): Observable<CentroSeleccionado | null> {
    return this.seleccion$.pipe(distinctUntilChanged(mismoCentro));
  }

  elegir(seleccion: CentroSeleccionado) {
    this.seleccion$.next(seleccion);
  }

  limpiar() {
    this.seleccion$.next(null);
  }

  /**
   * Toma la empresa y el centro de los parámetros cifrados de la URL.
   * @returns false si el enlace fue alterado o ya no es válido (la selección no cambia)
   */
  elegirDesdeRuta(empresa: string | null, centro: string | null): boolean {
    const int_empresa_id = this.descifrar(empresa);
    const int_centro_id = this.descifrar(centro);
    if (!int_empresa_id || !int_centro_id) return false;
    this.elegir({ int_empresa_id, int_centro_id });
    return true;
  }

  private descifrar(valor: string | null): number | null {
    const id = Number(valor ? this.urlCrypto.decryptId(valor) : null);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  // ---------------------------------------------------------------------------
  // Rutas
  // ---------------------------------------------------------------------------

  /** El módulo cuelga del layout principal (entrada propia en el menú lateral): aquí se eligen las empresas. */
  rutaModulo(): any[] {
    return ['/' + config.URL_BASE_PATH, 'vigilancia_salud'];
  }

  /** Raíz de un centro (por defecto, el elegido); sin centro elegido, la del módulo. */
  rutaCentro(seleccion: CentroSeleccionado | null = this.actual): any[] {
    if (!seleccion) return this.rutaModulo();
    return [
      ...this.rutaModulo(),
      RUTA_EMPRESA, this.urlCrypto.encryptId(seleccion.int_empresa_id),
      RUTA_CENTRO, this.urlCrypto.encryptId(seleccion.int_centro_id),
    ];
  }
}
